/**
 * Schémas zod **d'entrée** : tout ce que le client a le droit d'envoyer.
 *
 * Principe anti-triche (DECISIONS §8) : les schémas n'acceptent que des **faits de jeu**
 * (`spawnId` tués, emplacement choisi, carte choisie…). Aucun montant d'éclats, aucun niveau de
 * progression, aucun total de points n'est accepté en entrée. Les objets sont `.strict()` :
 * envoyer un champ non prévu (ex. `eclats: 999999`) produit `400 VALIDATION_FAILED`.
 *
 * Le serveur valide avec **ces** schémas, et le client s'en sert pour valider ses formulaires :
 * une seule vérité, deux usages (DECISIONS §4.1).
 */
import { z } from 'zod';
import { EXPORT_RUNS_LIMIT_DEFAUT, EXPORT_RUNS_LIMIT_MAX, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, RUNS_PAGE_LIMIT_DEFAUT, RUNS_PAGE_LIMIT_MAX, } from '../constants.js';
import { emailSchema, localeSchema, modeSchema, pseudoSchema } from './models.js';
/** Mot de passe : 10 caractères minimum (DECISIONS §4.2), 200 maximum (garde-fou de hachage). */
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `le mot de passe doit contenir au moins ${PASSWORD_MIN_LENGTH} caractères`)
    .max(PASSWORD_MAX_LENGTH, `le mot de passe ne peut pas dépasser ${PASSWORD_MAX_LENGTH} caractères`);
/** Message d'erreur dédié pour un consentement non coché. */
const consentementObligatoire = (nom) => ({
    errorMap: () => ({ message: `le consentement « ${nom} » est obligatoire` }),
});
/** Corps de `POST /auth/register`. */
export const registerInputSchema = z
    .object({
    email: emailSchema,
    password: passwordSchema,
    pseudo: pseudoSchema,
    locale: localeSchema,
    cguAccepted: z.literal(true, consentementObligatoire('CGU')),
    privacyAccepted: z.literal(true, consentementObligatoire('confidentialité')),
})
    .strict();
/** Corps de `POST /auth/login` — pas de longueur minimale ici : une saisie courte est un échec, pas un 400. */
export const loginInputSchema = z
    .object({
    email: emailSchema,
    password: z.string().min(1, 'le mot de passe est obligatoire').max(PASSWORD_MAX_LENGTH),
})
    .strict();
/** Corps de `POST /auth/email/verify`. */
export const emailVerifyInputSchema = z.object({ token: z.string().min(16).max(512) }).strict();
/** Corps de `POST /auth/password/forgot`. */
export const passwordForgotInputSchema = z.object({ email: emailSchema }).strict();
/** Corps de `POST /auth/password/reset`. */
export const passwordResetInputSchema = z
    .object({ token: z.string().min(16).max(512), password: passwordSchema })
    .strict();
/** Corps de `PATCH /me` : au moins un champ, jamais un champ vide. */
export const updateMeInputSchema = z
    .object({
    pseudo: pseudoSchema.optional(),
    locale: localeSchema.optional(),
})
    .strict()
    .refine((valeur) => valeur.pseudo !== undefined || valeur.locale !== undefined, {
    message: 'au moins un champ (pseudo, locale) est attendu',
});
/** Corps de `POST /runs`. */
export const runCreateInputSchema = z
    .object({
    mode: modeSchema,
    levelId: z.string().min(1),
    /** Version de contenu que le client s'apprête à jouer : refusée si elle n'est plus publiée. */
    contentVersion: z.string().min(1),
})
    .strict();
/**
 * Corps de `POST /runs/:runId/waves/:index/start` : **aucun champ**.
 *
 * La composition de la vague est décidée par le serveur (DECISIONS §8.1) ; le client ne peut
 * donc rien proposer, pas même un nombre d'ennemis.
 */
export const waveStartInputSchema = z.object({}).strict();
/**
 * Corps de `POST /runs/:runId/waves/:index/result`.
 *
 * Le client n'envoie que des **faits** : les `spawnId` tués, ceux qui ont atteint la sortie,
 * et les PV du parcours restants (qui doivent être cohérents avec les fuites).
 */
export const waveResultInputSchema = z
    .object({
    /** Identifiants uniques des ennemis éliminés dans la vague. */
    ennemisTues: z.array(z.string().min(1)).max(2000),
    /** Identifiants uniques des ennemis arrivés à la sortie. */
    fuites: z.array(z.string().min(1)).max(2000),
    /** PV du parcours restants, recoupés par le serveur (DECISIONS §8.3c). */
    pvParcoursRestant: z.number().int().min(0).max(10_000),
})
    .strict();
/** Corps de `POST /runs/:runId/cards/options`. */
export const cardOptionsInputSchema = z
    .object({ waveIndex: z.number().int().nonnegative() })
    .strict();
/** Corps de `POST /runs/:runId/cards/choose`. */
export const cardChooseInputSchema = z
    .object({
    waveIndex: z.number().int().nonnegative(),
    cardId: z.string().min(1),
})
    .strict();
/**
 * Corps de `POST /runs/:runId/towers` (contrat M2, DECISIONS §4.2).
 *
 * Le coût est **entièrement recalculé côté serveur** (prix de la tour, réduction éventuelle,
 * joker) : aucun champ monétaire n'est accepté. `jokerConsomme` est un fait de jeu (pose
 * pendant une vague), pas un montant.
 */
export const runPlaceTowerInputSchema = z
    .object({
    towerId: z.string().min(1),
    emplacementId: z.string().min(1),
    /** `true` = pose pendant une vague : prix de la tour + 1 joker (DECISIONS §1.3 A-18). */
    jokerConsomme: z.boolean(),
})
    .strict();
/** Alias M1 conservé pour ne pas casser les imports existants (même schéma, même objet). */
export const towerPlaceInputSchema = runPlaceTowerInputSchema;
/**
 * Corps de `POST /runs/:runId/towers/:towerId/upgrade` : **aucun champ**.
 *
 * Le niveau cible et le coût sont calculés par le serveur (`coutParNiveau × niveau`,
 * DECISIONS §1.3 A-6) : accepter un `niveau` serait un défaut de sécurité (§8.8).
 */
export const runUpgradeInputSchema = z.object({}).strict();
/** Alias M1 conservé pour ne pas casser les imports existants (même schéma, même objet). */
export const towerUpgradeInputSchema = runUpgradeInputSchema;
/** Corps de `POST /runs/:runId/end`. */
export const runEndInputSchema = z
    .object({ reason: z.enum(['defaite', 'victoire', 'abandon']) })
    .strict();
/**
 * Paramètres de `POST /me/upgrades/:upgradeId` (achat d'une amélioration permanente).
 *
 * Le corps de la requête est vide (`{}`) et le **niveau acheté est toujours le suivant** :
 * `upgradeId` ne désigne que le catalogue, jamais un niveau ni un montant.
 */
export const upgradeBuyParamsSchema = z
    .object({
    upgradeId: z
        .string()
        .regex(/^[a-z][a-z0-9_]+$/, 'identifiant d’amélioration attendu : snake_case'),
})
    .strict();
/** Query de `GET /me/runs`. */
export const meRunsQuerySchema = z
    .object({
    limit: z.coerce.number().int().min(1).max(RUNS_PAGE_LIMIT_MAX).default(RUNS_PAGE_LIMIT_DEFAUT),
})
    .strict();
/**
 * Query de `GET /me/export` — **seul** paramètre accepté : `limit`.
 *
 * Le schéma est `.strict()` : `?userId=…` ou tout autre clé produit `400 VALIDATION_FAILED`.
 * L'export ne peut donc pas désigner un autre joueur, même par erreur (défense en
 * profondeur : le contrôleur n'utilise que l'identité du jeton).
 */
export const meExportQuerySchema = z
    .object({
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(EXPORT_RUNS_LIMIT_MAX)
        .default(EXPORT_RUNS_LIMIT_DEFAUT),
})
    .strict();
/** Query de `GET /admin/players` (lecture seule, paginée). */
export const adminPlayersQuerySchema = z
    .object({
    limit: z.coerce.number().int().min(1).max(RUNS_PAGE_LIMIT_MAX).default(RUNS_PAGE_LIMIT_DEFAUT),
    offset: z.coerce.number().int().min(0).default(0),
})
    .strict();
//# sourceMappingURL=validation.js.map